import React, { useEffect, useState } from "react";
import LocalizedText from '../../presentational/LocalizedText';
import useTranslate from "../../../hooks/useTranslate";
import Checkbox from "../../presentational/Checkbox";
import Button from "../../presentational/Button";
import Input from "../../presentational/Input";
import PasswordTippy from "../../PasswordTippy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTurnstile } from "../../../hooks/useTurnstile";

interface SetPasswordProps {
  serverError: string;
  isLoading: boolean;
  emailOrPhone: string;
  rememberMe: boolean;
  productName: string;
  finishJoin: (password: string) => void;
  validatePasswordRequirements: (password: string) => Promise<string>;
  isValidPassword: (password: string) => boolean;
  setEmailOrPhone: (emailOrPhone: string) => void;
  setRememberMe: (rememberMe: boolean) => void;
  notYouLink?: string;
}

const SetPassword: React.FC<SetPasswordProps> = ({
  serverError,
  isLoading,
  emailOrPhone,
  rememberMe,
  productName,
  finishJoin,
  validatePasswordRequirements,
  isValidPassword,
  setEmailOrPhone,
  setRememberMe,
  notYouLink,
}) => {
  const [password, setPassword] = useState('')
  const [unmaskPassword, setUnmaskPassword] = useState(false)
  const [passwordValid, setPasswordValid] = useState(false);
    const translations = useTranslate([
      "participant-login-password-label", 
      "participant-login-password-placeholder", 
      "participant-login-remember-me", 
      "participant-login-next-label",
      "participant-login-password-requirements-title",
      "participant-login-password-requirement-length",
      "participant-login-password-requirement-classes",
    ])
  useTurnstile();

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleChangeRememberMe = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    finishJoin(password)
    return false
  }

  const handleNotYouClick = () => {
    setEmailOrPhone('')
    setRememberMe(false)
  }

  return (
    <form className="login-action set-password" onSubmit={handleSubmit}>
      <h3><LocalizedText textKey="participant-login-set-password-title" /> <span>{productName}</span></h3>
      <p className="personalized-welcome-back">
        <span className="personalized-welcome-back-userid">{emailOrPhone}</span>
        {
          notYouLink && (
            <Button variant="minimal" intent="primary" to={notYouLink} onClick={handleNotYouClick}><LocalizedText textKey="participant-login-not-you-label" /></Button>
          )
        }
        <LocalizedText textKey="participant-login-set-password-info" values={{ emailOrPhone: emailOrPhone }} />
        
      </p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <div className="field-group">
        <label>{translations["participant-login-password-label"]}</label>
        <div className="combo-input-group">
          <PasswordTippy
            password={password}
            passwordValid={passwordValid}
            setPasswordValid={setPasswordValid}
            validatePasswordRequirements={validatePasswordRequirements}
            requirementsTitle={translations["participant-login-password-requirements-title"]}
            requirementLengthText={translations["participant-login-password-requirement-length"]}
            requirementClassesText={translations["participant-login-password-requirement-classes"]}>
            <Input
              onChange={handleChangePassword}
              autoComplete="new-password"
              autoFocus={true}
              className="autofocus"
              disabled={isLoading}
              id="resetPassword"
              type={unmaskPassword ? 'text' : 'password'}
              value={password}
              placeholder={translations["participant-login-password-placeholder"]}
              fullWidth={true}
              htmlAttributes={{
                onInput: handleChangePassword,
                spellCheck: false,
                autoCorrect: "off",
                autoCapitalize: "none",
                maxLength: 64,
              }}
            />
          </PasswordTippy>
          <button
            type="button"
            onClick={() => setUnmaskPassword(!unmaskPassword)}
            className="password-mask-toggle"
            title="Toggle password visibility">
               <FontAwesomeIcon icon={unmaskPassword ? faEye as IconProp: faEyeSlash as IconProp} />
          </button>
        </div>
      </div>
      <Checkbox
        className="login-field checkbox-field"
        identifier="rememberMe"
        checked={rememberMe}
        onChange={handleChangeRememberMe}
        label={translations["participant-login-remember-me"]}
      />
      <div>
        <div id="cf-turnstile" className="cf-turnstile"></div>
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={!isValidPassword(password)}
          intent='primary'
          className="primary-action"
          width="100%"
          loading={isLoading}
        >
          <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
        </Button>
      </div>
    </form>
  )
}

export default SetPassword